import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, filter, fromEvent, interval, merge, of, switchMap } from 'rxjs';

type VersionResponse = {
  version: string;
  builtAt?: string;
};

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly http = inject(HttpClient);
  private currentVersion: string | null = null;
  private started = false;

  readonly updateAvailable = signal(false);
  readonly availableBuiltAt = signal<string | null>(null);

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.fetchVersion().subscribe((version) => {
      this.currentVersion = version?.version ?? null;
    });

    merge(
      interval(3 * 60 * 1000),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
    )
      .pipe(
        filter(() => !this.updateAvailable()),
        switchMap(() => this.fetchVersion()),
      )
      .subscribe((version) => {
        if (!version || !this.currentVersion || version.version === this.currentVersion) {
          return;
        }

        this.availableBuiltAt.set(version.builtAt ?? null);
        this.updateAvailable.set(true);
      });
  }

  private fetchVersion() {
    return this.http
      .get<VersionResponse>(`/version.json?_=${Date.now()}`)
      .pipe(catchError(() => of<VersionResponse | null>(null)));
  }
}
